import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Briefcase,
  FileText,
  Mail,
  CheckSquare2,
  Calendar,
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  Filter,
  CheckCircle2,
  ExternalLink,
  FileSignature,
  History,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { formatINR } from "@/lib/utils";
import type { DealRecord, QuotationRecord } from "@/types";
import type { DealEmailRecord } from "@/types/email";
import type { WhatsAppThread } from "@/types/whatsapp";
import type { Task } from "@/types/task";
import type { CustomerHistoryItem } from "@/types";

export type TimelineFilter = "ALL" | "DEALS" | "HISTORY" | "QUOTES" | "EMAILS" | "WHATSAPP" | "TASKS";

export interface ActivityTimelineProps {
  deals?: DealRecord[];
  quotations?: QuotationRecord[];
  emails?: DealEmailRecord[];
  whatsapp?: WhatsAppThread[];
  tasks?: Task[];
  /** Deal history: stage moves, won/lost, calls and meetings, samples, milestones */
  history?: CustomerHistoryItem[];
  onOpenWhatsApp?: (thread: WhatsAppThread) => void;
}

interface TimelineItem {
  id: string;
  kind: "DEAL" | "HISTORY" | "QUOTE" | "EMAIL" | "WHATSAPP" | "TASK";
  timestamp: number;
  dateStr: string;
  title: string;
  subtitle?: string;
  badge: { label: string; color: string; bg: string; border: string };
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  raw: any;
  link?: string;
}

const HISTORY_LABELS: Record<string, string> = {
  STAGE_CHANGE: "STAGE",
  MILESTONE: "MILESTONE",
  SAMPLE: "SAMPLE",
  ORDER: "ORDER",
  COMPLAINT: "COMPLAINT",
  QUALIFICATION: "QUALIFICATION",
  DEAL_UPDATE: "UPDATE",
  TASK_COMPLETED: "TASK DONE",
  ACTIVITY_DELETED: "REMOVED",
};

export function ActivityTimeline({
  deals = [],
  quotations = [],
  emails = [],
  whatsapp = [],
  tasks = [],
  history = [],
  onOpenWhatsApp,
}: ActivityTimelineProps) {
  const [activeFilter, setActiveFilter] = useState<TimelineFilter>("ALL");

  const items = useMemo(() => {
    const list: TimelineItem[] = [];

    // 1. Deals
    for (const d of deals) {
      const time = new Date(d.created_on || Date.now()).getTime();
      list.push({
        id: `deal-${d.id}`,
        kind: "DEAL",
        timestamp: time,
        dateStr: d.created_on || new Date().toISOString(),
        title: `Deal created: ${d.name}`,
        subtitle: `Amount: ${formatINR(d.amount)} • Stage: ${d.stage?.name || "Initial"}`,
        badge: {
          label: d.deal_status,
          color: d.deal_status === "WON" ? "text-emerald-600 dark:text-emerald-400" : d.deal_status === "LOST" ? "text-rose-600 dark:text-rose-400" : "text-blue-600 dark:text-blue-400",
          bg: d.deal_status === "WON" ? "bg-emerald-500/10" : d.deal_status === "LOST" ? "bg-rose-500/10" : "bg-blue-500/10",
          border: d.deal_status === "WON" ? "border-emerald-500/30" : d.deal_status === "LOST" ? "border-rose-500/30" : "border-blue-500/30",
        },
        icon: Briefcase,
        iconBg: "bg-blue-500 text-white",
        raw: d,
        link: `/deals/${d.id}`,
      });
    }

    // 1b. Deal history (stage moves, won/lost, logged calls and meetings, sample and order events)
    for (const h of history) {
      const label = HISTORY_LABELS[h.change_type] || h.change_type.replace(/_/g, " ");
      list.push({
        id: `history-${h.id}`,
        kind: "HISTORY",
        timestamp: new Date(h.created_on).getTime(),
        dateStr: h.created_on,
        title: h.title,
        subtitle: [h.deal_name && `Deal: ${h.deal_name}`, h.by && `by ${h.by}`, h.description].filter(Boolean).join(" • "),
        badge: { label, color: "text-slate-600 dark:text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/30" },
        icon: History,
        iconBg: "bg-slate-500 text-white",
        raw: h,
        link: `/deals/${h.deal_id}`,
      });
    }

    // 2. Quotations
    for (const q of quotations) {
      const time = new Date(q.created_on || Date.now()).getTime();
      list.push({
        id: `quote-${q.id}`,
        kind: "QUOTE",
        timestamp: time,
        dateStr: q.created_on || new Date().toISOString(),
        title: `Quotation issued: ${q.display_number || q.quote_number}`,
        subtitle: `Total: ${formatINR(q.grand_total)}${q.deal ? ` • Deal: ${q.deal.name}` : ""}`,
        badge: {
          label: q.quote_status,
          color: "text-amber-600 dark:text-amber-400",
          bg: "bg-amber-500/10",
          border: "border-amber-500/30",
        },
        icon: FileText,
        iconBg: "bg-amber-500 text-white",
        raw: q,
        link: `/quotes/${q.id}`,
      });
    }

    // 3. Emails
    for (const e of emails) {
      const time = new Date(e.email_date || Date.now()).getTime();
      const isIncoming = e.direction === "IN";
      list.push({
        id: `email-${e.id}`,
        kind: "EMAIL",
        timestamp: time,
        dateStr: e.email_date,
        title: `${isIncoming ? "Received email" : "Sent email"}: ${e.subject || "(no subject)"}`,
        subtitle: isIncoming ? `From: ${e.from_name || e.from_address}` : `To: ${e.to_addresses.join(", ")}`,
        badge: {
          label: isIncoming ? "INCOMING" : "SENT",
          color: isIncoming ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400",
          bg: isIncoming ? "bg-emerald-500/10" : "bg-rose-500/10",
          border: isIncoming ? "border-emerald-500/30" : "border-rose-500/30",
        },
        icon: Mail,
        iconBg: "bg-rose-500 text-white",
        raw: e,
      });
    }

    // 4. WhatsApp conversations
    for (const t of whatsapp) {
      const conv = t.conversation;
      const lastMsg = t.messages[t.messages.length - 1];
      const time = new Date(conv.last_message_at || Date.now()).getTime();
      list.push({
        id: `wa-${conv.id}`,
        kind: "WHATSAPP",
        timestamp: time,
        dateStr: conv.last_message_at || new Date().toISOString(),
        title: `WhatsApp chat: ${conv.profile_name || conv.phone}`,
        subtitle: lastMsg ? `${lastMsg.direction === "IN" ? "Customer" : "Agent"}: ${lastMsg.body || lastMsg.caption || `[${lastMsg.message_type}]`}` : "Chat initialized",
        badge: {
          label: conv.window_open ? "WINDOW OPEN" : "WHATSAPP",
          color: "text-[#128C7E] dark:text-[#25D366]",
          bg: "bg-[#25D366]/10",
          border: "border-[#25D366]/30",
        },
        icon: WhatsAppIcon as any,
        iconBg: "bg-[#25D366] text-white",
        raw: t,
      });
    }

    // 5. Tasks: placed when they were done (or created), never at a future due date
    for (const task of tasks) {
      const isDone = task.task_status === "DONE";
      const when = (isDone && task.completed_at) || task.created_on || task.due_at;
      const due = task.due_at ? new Date(task.due_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;
      list.push({
        id: `task-${task.id}`,
        kind: "TASK",
        timestamp: new Date(when).getTime(),
        dateStr: when,
        title: `${isDone ? "Task done" : "Task created"}: ${task.title}`,
        subtitle: `Type: ${task.task_type} • Priority: ${task.priority}${due ? ` • Due ${due}` : ""}${task.assigned_to ? ` • Assigned to: ${task.assigned_to.first_name}` : ""}`,
        badge: {
          label: isDone ? "COMPLETED" : "PENDING",
          color: isDone ? "text-emerald-600 dark:text-emerald-400" : "text-indigo-600 dark:text-indigo-400",
          bg: isDone ? "bg-emerald-500/10" : "bg-indigo-500/10",
          border: isDone ? "border-emerald-500/30" : "border-indigo-500/30",
        },
        icon: CheckSquare2,
        iconBg: isDone ? "bg-emerald-500 text-white" : "bg-indigo-500 text-white",
        raw: task,
      });
    }

    return list.sort((a, b) => b.timestamp - a.timestamp);
  }, [deals, quotations, emails, whatsapp, tasks, history]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "ALL") return items;
    if (activeFilter === "DEALS") return items.filter((i) => i.kind === "DEAL");
    if (activeFilter === "HISTORY") return items.filter((i) => i.kind === "HISTORY");
    if (activeFilter === "QUOTES") return items.filter((i) => i.kind === "QUOTE");
    if (activeFilter === "EMAILS") return items.filter((i) => i.kind === "EMAIL");
    if (activeFilter === "WHATSAPP") return items.filter((i) => i.kind === "WHATSAPP");
    if (activeFilter === "TASKS") return items.filter((i) => i.kind === "TASK");
    return items;
  }, [items, activeFilter]);

  const filterTabs: Array<{ id: TimelineFilter; label: string; count: number }> = [
    { id: "ALL", label: "All Activity", count: items.length },
    { id: "DEALS", label: "Deals", count: deals.length },
    ...(history.length ? [{ id: "HISTORY" as TimelineFilter, label: "Deal history", count: history.length }] : []),
    { id: "QUOTES", label: "Quotes", count: quotations.length },
    { id: "EMAILS", label: "Emails", count: emails.length },
    { id: "WHATSAPP", label: "WhatsApp", count: whatsapp.length },
    { id: "TASKS", label: "Tasks", count: tasks.length },
  ];

  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      {/* Top Filter Bar */}
      <div className="px-5 py-4 border-b border-border/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-primary" />
          <h3 className="text-sm font-semibold tracking-tight text-foreground">
            Unified Activity Timeline
          </h3>
          <span className="text-xs text-muted-foreground font-mono">({items.length} events)</span>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {filterTabs.map((ft) => {
            const isActive = activeFilter === ft.id;
            return (
              <button
                key={ft.id}
                type="button"
                onClick={() => setActiveFilter(ft.id)}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors font-medium shrink-0 flex items-center gap-1.5 ${
                  isActive
                    ? "bg-primary text-primary-foreground font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <span>{ft.label}</span>
                <span className={`text-[10px] px-1 py-0 rounded-full ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  {ft.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Timeline Feed */}
      {filteredItems.length === 0 ? (
        <div className="p-12 text-center space-y-2">
          <Clock className="size-8 text-muted-foreground mx-auto stroke-1" />
          <p className="text-sm font-medium text-foreground">No activities recorded</p>
          <p className="text-xs text-muted-foreground">
            No activity matches the selected filter.
          </p>
        </div>
      ) : (
        <div className="p-6 relative">
          {/* Vertical Connecting Line */}
          <div className="absolute left-[39px] top-8 bottom-8 w-0.5 bg-border/80" />

          <div className="space-y-6">
            {filteredItems.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="flex items-start gap-4 relative group">
                  {/* Icon Node */}
                  <div
                    className={`size-8 rounded-full flex items-center justify-center shrink-0 ring-4 ring-card shadow-2xs z-10 ${item.iconBg}`}
                  >
                    <Icon className="size-4" />
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 bg-background border border-border/70 rounded-xl p-3.5 shadow-2xs hover:border-border transition-colors space-y-1.5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className={`text-[10px] font-bold px-1.5 py-0 ${item.badge.bg} ${item.badge.color} ${item.badge.border}`}
                        >
                          {item.badge.label}
                        </Badge>
                        {item.link ? (
                          <Link
                            to={item.link}
                            className="font-semibold text-xs text-foreground hover:text-primary hover:underline inline-flex items-center gap-1"
                          >
                            <span>{item.title}</span>
                            <ExternalLink className="size-3 text-muted-foreground" />
                          </Link>
                        ) : item.kind === "WHATSAPP" && onOpenWhatsApp ? (
                          <button
                            type="button"
                            onClick={() => onOpenWhatsApp(item.raw)}
                            className="font-semibold text-xs text-foreground hover:text-primary hover:underline text-left inline-flex items-center gap-1 cursor-pointer"
                          >
                            <span>{item.title}</span>
                            <ExternalLink className="size-3 text-muted-foreground" />
                          </button>
                        ) : (
                          <span className="font-semibold text-xs text-foreground">
                            {item.title}
                          </span>
                        )}
                      </div>

                      <span className="text-[11px] text-muted-foreground font-mono shrink-0">
                        {new Date(item.dateStr).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>

                    {item.subtitle && (
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
